import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addCompIntermediateSoldToBusiness,
  getCompIntermediateSoldList,
  updateCompIntermediateSold,
  deleteCompIntermediateSold,
} from "../../../../../../apis/Business/CompanyIntermediateUseOfSoldApi";
import {
  getCompIntermediateEnergyDropdown,
  getTransportCountryData,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const IntermediateUseSoldTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownProcess, setDropdownProcess] = useState([]);
  const [dropdownCountry, setDropdownCountry] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getCompIntermediateSoldList({
      businessId,
      setObjects: setData,
    });
    getCompIntermediateEnergyDropdown({ setDropdown: setDropdownProcess });
    getTransportCountryData({ setDropdownData: setDropdownCountry });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownProcess?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["fuel_source.value"]?.value || row.fuel_source?.value;

        if (materialValue) {
          const selectedMaterial = dropdownProcess.find(
            (option) =>
              option?.dropdown === materialValue ||
              option?.dropdown_en === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownProcess]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "fuel_source.value": {
        targetField: "amount_per_use",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedItem = dropdownData.find(
            (item) =>
              item.dropdown === selectedValue ||
              item.dropdown_en === selectedValue
          );
          if (selectedItem) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedItem.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedItem.unit,
              }));
            }
          }
        },
        dropdownDataKey: "fuel_source.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      updateCompIntermediateSold({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        fuel_source: editedItem["fuel_source.value"]
          ? editedItem["fuel_source.value"].value
          : editedItem["fuel_source"]
          ? editedItem["fuel_source"].value
          : null,
        country_end_product: editedItem["country_end_product.value"]
          ? editedItem["country_end_product.value"].value
          : editedItem["country_end_product"]
          ? editedItem["country_end_product"].value
          : null,
        amount_per_use: editedItem["customMulti_amount_per_use"]
          ? editedItem["customMulti_amount_per_use"]
          : editedItem["amount_per_use"],
        amount_per_lifetime: editedItem["customMulti_amount_per_lifetime"]
          ? editedItem["customMulti_amount_per_lifetime"]
          : editedItem["amount_per_lifetime"],
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addCompIntermediateSoldToBusiness({
        businessId,
        ...newItem,
        fuel_source: newItem["fuel_source.value"]
          ? newItem["fuel_source.value"].value
          : null,
        country_end_product: newItem["country_end_product.value"]
          ? newItem["country_end_product.value"].value
          : null,
        amount_per_use: newItem["customMulti_amount_per_use"],
        amount_per_lifetime: newItem["customMulti_amount_per_lifetime"],
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteCompIntermediateSold({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "product_name",
        header: t("PRODUCT_NAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "client",
        header: t("COMP_INTERMED_CLIENT"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "end_product",
        header: t("COMP_INTERMED_COMPONENT_PART"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
        tipHeader: "Tip",
        tipBody: t("INTERMED_COMPONENT_PART_TIP"),
      },
      {
        accessor: "end_product_num",
        header: t("COMP_INTERMED_PRODUCT_NUM"),
        type: "text",
        disableSortBy: true,
        placeholder: 0,
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("INTERMED_NO_END_PROD_TIP"),
      },
      {
        accessor: "country_end_product.value",
        header: `${t("COUNTRY_PREDOMINENT_USED")}`,
        type: "dropdown",
        dropdownData: dropdownCountry,
        disableSortBy: true,
        width: "130px",
      },
      {
        accessor: "total_weight",
        header: `${t("COMP_INTERMED_TOTAL_WEIGHT")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
      },
      {
        accessor: "weight",
        header: `${t("COMP_INTERMED_WEIGHT")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("INTERMED_WEIGHT_TIP"),
      },
      {
        accessor: "times_end_product_used",
        header: `${t("COMP_INTERMED_PROD_TIMES")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
        isNABtn: true,
        tipHeader: "Tip",
        tipBody: t("INTERMED_END_USED_TIP"),
      },
      {
        accessor: "fuel_source.value",
        header: `${t("COMP_ENERGY_DIRECT_USED")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "150px",
        tipHeader: "Tip",
        tipBody: t("INTERMED_FUEL_TIP"),
      },

      {
        accessor: "customMulti",
        header: `${t("COMP_EXPECTED_AMOUNT")}`,
        type: "multipleCustom",
        wide: true,
        layout: "horizontal",
        optional: true,
        width: "300px",
        tipHeader: "Tip",
        tipBody: t("INTERMED_EXPECTED_TIP"),
        customInputs: [
          {
            accessor: "amount_per_use",
            header: `${t("per use")}`,
            type: "number",
            disableSortBy: true,
            optional: true,
            placeholder: "0",
            width: "60px",
            unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
            additionalText: `${t("per use")}`,
            isNA: true,
            accessorNA: "times_end_product_used",
          },
          {
            accessor: "amount_per_lifetime",
            header: `${t("per lifetime")}`,
            type: "number",
            optional: true,
            width: "60px",
            disableSortBy: true,
            unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
            additionalText: `${t("per life")}`,
            isNA: false,
            accessorNA: "times_end_product_used",
          },
        ],
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [dropdownProcess, dropdownCountry, unitMaterials, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "fuel_source.value": dropdownProcess,
      "country_end_product.value": dropdownCountry,
    }),
    [dropdownProcess, dropdownCountry]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default IntermediateUseSoldTable;
